import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Modal, Form, Input, message, Popconfirm, AutoComplete } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { ContentItem, ContentItemCreateInput, ContentItemUpdateInput } from '../types/contentItem';
import contentItemAPI from '../services/contentItemAPI';
import { ColumnsType } from 'antd/es/table';
import { tokenManager } from '../services/api';

const { TextArea } = Input;

// 기본 제공 콘텐츠 링크 목록
const CONTENT_LINK_OPTIONS = [
  { value: '/tenant-intro', label: '입주사 소개 (/tenant-intro)' },
  { value: '/notices', label: '공지사항 (/notices)' },
  { value: '/restaurants', label: '맛집 가이드 (/restaurants)' },
  { value: '/popups', label: '팝업 가이드 (/popups)' },
  { value: '/media-management', label: '미디어 관리 (/media-management)' },
  { value: '/event-management', label: '이벤트 관리 (/event-management)' },
  { value: '/interaction-management', label: '인터랙션 (/interaction-management)' },
  { value: '/greeting-management', label: '그리팅 (/greeting-management)' },
  { value: '/api-management', label: '뉴스, 공연, 전시, 날씨 (/api-management)' },
];

const ContentItemManagement: React.FC = () => {
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<ContentItem | null>(null);
  const [form] = Form.useForm();

  // 현재 사용자 정보 가져오기
  const currentUser = tokenManager.getUser();
  const isDeveloperAdmin = currentUser?.role === 'development_admin';

  // 콘텐츠 아이템 목록 로드
  const loadContentItems = async () => {
    try {
      setLoading(true);
      const response = await contentItemAPI.getContentItems({
        page: 1,
        limit: 1000
      });
      setContentItems(response.data);
    } catch (error: any) {
      message.error(error.response?.data?.message || '콘텐츠 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContentItems();
  }, []);

  // 모달 열기 (추가/수정)
  const openModal = (item?: ContentItem) => {
    if (item) {
      setEditingItem(item);
      form.setFieldsValue({
        contentTitle: item.contentTitle,
        duration: item.duration,
        description: item.description,
        linkUrl: item.linkUrl
      });
    } else {
      setEditingItem(null);
      form.resetFields();
      form.setFieldsValue({ duration: '00:30' });
    }
    setModalVisible(true);
  };

  // 모달 닫기
  const closeModal = () => {
    setModalVisible(false);
    setEditingItem(null);
    form.resetFields();
  };

  // 저장
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const dataToSave = { ...values, isActive: true };

      if (editingItem) {
        // 수정
        await contentItemAPI.updateContentItem(editingItem.id, dataToSave as ContentItemUpdateInput);
        message.success('성공적으로 저장되었습니다.');
      } else {
        // 추가
        await contentItemAPI.createContentItem(dataToSave as ContentItemCreateInput);
        message.success('성공적으로 추가되었습니다.');
      }

      closeModal();
      loadContentItems();
    } catch (error: any) {
      if (error.errorFields) {
        message.error('필수 항목을 입력해주세요.');
      } else {
        message.error(error.response?.data?.message || '저장에 실패했습니다.');
      }
    }
  };

  // 삭제
  const handleDelete = async (id: number) => {
    try {
      await contentItemAPI.deleteContentItem(id);
      message.success('삭제가 완료되었습니다.');
      loadContentItems();
    } catch (error: any) {
      message.error(error.response?.data?.message || '삭제에 실패했습니다.');
    }
  };

  // 테이블 컬럼 정의
  const columns: ColumnsType<ContentItem> = [
    {
      title: '콘텐츠 제목',
      dataIndex: 'contentTitle',
      key: 'contentTitle',
      ellipsis: true,
      width: 300
    },
    {
      title: '노출 시간',
      dataIndex: 'duration',
      key: 'duration',
      width: 100,
      render: (duration: string) => <Tag>{duration}</Tag>
    },
    {
      title: '설명',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text: string) => text || '-'
    }
  ];

  return (
    <div>
      {isDeveloperAdmin && (
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'flex-end' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            콘텐츠 추가
          </Button>
        </Space>
      )}

      <Table
        columns={columns}
        dataSource={contentItems}
        rowKey="id"
        loading={loading}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `총 ${total}개`
        }}
        onRow={(record) => ({
          onClick: () => openModal(record),
          style: { cursor: 'pointer' }
        })}
      />

      <Modal
        title={editingItem ? '콘텐츠 수정' : '콘텐츠 추가'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={closeModal}
        width={600}
        destroyOnHidden={true}
        maskClosable={true}
        styles={{
          mask: {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(10px)'
          }
        }}
        closeIcon={<span style={{ color: '#ffffff' }}>✕</span>}
        footer={[
          editingItem && (
            <Popconfirm
              key="delete"
              title="정말 삭제하시겠습니까?"
              onConfirm={() => {
                handleDelete(editingItem.id);
                closeModal();
              }}
              okText="삭제"
              cancelText="취소"
            >
              <Button danger style={{ float: 'left' }}>
                삭제
              </Button>
            </Popconfirm>
          ),
          <Button key="cancel" onClick={closeModal}>
            취소
          </Button>,
          <Button key="submit" type="primary" onClick={handleSave}>
            저장
          </Button>
        ]}
      >
        <Form
          form={form}
          layout="vertical"
          style={{ marginTop: 20 }}
        >
          <Form.Item
            name="contentTitle"
            label="콘텐츠 제목"
            rules={[
              { required: true, message: '콘텐츠 제목을 입력해주세요.' },
              { max: 200, message: '제목은 200자 이내로 입력해주세요.' }
            ]}
          >
            <Input placeholder="콘텐츠 제목" />
          </Form.Item>

          <Form.Item
            name="duration"
            label="노출 시간 (mm:ss)"
            rules={[
              { required: true, message: '노출 시간을 입력해주세요.' },
              { pattern: /^\d{2}:\d{2}$/, message: 'mm:ss 형식으로 입력해주세요. (예: 00:30)' }
            ]}
          >
            <Input placeholder="00:30" />
          </Form.Item>

          <Form.Item
            name="description"
            label="설명"
          >
            <TextArea
              rows={4}
              placeholder="콘텐츠 설명 (선택사항)"
            />
          </Form.Item>

          <Form.Item
            name="linkUrl"
            label="링크 URL"
            rules={[
              { max: 500, message: 'URL은 500자 이내로 입력해주세요.' }
            ]}
            tooltip="목록에서 선택하거나 직접 입력할 수 있습니다"
          >
            <AutoComplete
              options={CONTENT_LINK_OPTIONS}
              placeholder="링크를 선택하거나 직접 입력하세요 (예: /tenant-intro)"
              filterOption={(inputValue, option) =>
                option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1 ||
                option!.label.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
              }
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ContentItemManagement;
